//--- ARQUIVO NOVO E COMPLETO: utils.js ---

/**
 * Formata um valor numérico (presumido ser um float/decimal) para moeda BRL.
 * @param {number} value O valor numérico a ser formatado.
 * @returns {string} O valor formatado como string (ex: "R$ 1.234,56").
 */
 export const formatCurrencyDisplay = (value) => {
    // Garante que temos um número, tratando null/undefined como 0
    const numericValue = typeof value === 'number' ? value : 0;
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(numericValue);
  };
  
  /**
  * Formata uma string de data ISO "AAAA-MM-DD" para o formato "DD/MM/AAAA".
  * @param {string} isoString A data no formato "AAAA-MM-DD".
  * @returns {string} A data formatada ou uma string vazia/indicativa de erro.
  */
  export const formatDateDisplay = (isoString) => {
    if (!isoString || typeof isoString !== 'string') return ''; // Retorna vazio se a entrada for inválida
    try {
        // Adiciona T00:00:00 para garantir interpretação na timezone local
        // Divide por '-' para evitar problemas com formatos de data
        const parts = isoString.split('-');
        if (parts.length !== 3) return 'Inválida'; // Verifica se o formato parece correto
        const date = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
        // Verifica se a data criada é válida
        if (isNaN(date.getTime())) return 'Inválida';
        return date.toLocaleDateString('pt-BR');
    } catch (e) {
        console.error("Erro ao formatar data:", isoString, e);
        return 'Erro Data'; // Retorna uma string indicativa de erro
    }
  };
  
  
  /**
  * Calcula o saldo atual de uma conta específica.
  *
  * @param {string} accountId O ID da conta para calcular o saldo.
  * @param {Array} allAccounts Array contendo todos os objetos de conta.
  * @param {Array} allTransactions Array contendo todos os objetos de transação.
  * @returns {number | null} O saldo atual calculado ou null se a conta não for encontrada.
  */
  export const calculateAccountBalance = (accountId, allAccounts, allTransactions) => {
    // 1. Encontra a conta específica
    const account = allAccounts.find(acc => acc.id === accountId);
  
    // 2. Se a conta não for encontrada, retorna null
    if (!account) {
        console.warn(`Conta com ID ${accountId} não encontrada para cálculo de saldo.`);
        return null;
    }
  
    // 3. Obtém o saldo inicial e a data de abertura
    // Garante que startingBalance seja um número, default 0 se não for
    const startingBalance = typeof account.startingBalance === 'number' ? account.startingBalance : 0;
    let currentBalance = startingBalance;
    const openingDateStr = account.openingDate; // Ex: "2023-10-01"
  
    // Se não houver data de abertura válida, não podemos calcular corretamente
    if (!openingDateStr) {
        console.warn(`Conta ${account.name} (${accountId}) não tem data de abertura válida.`);
        // Decide se retorna o saldo inicial ou null/erro. Retornar saldo inicial pode ser enganoso.
        return startingBalance; // Ou talvez retornar null? Por enquanto, retorna o saldo inicial.
    }
  
    // Convertendo a data de abertura para um objeto Date (considerando meia-noite local)
    let openingDate;
    try {
        const parts = openingDateStr.split('-');
        openingDate = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
         if (isNaN(openingDate.getTime())) throw new Error("Data inválida");
         openingDate.setHours(0, 0, 0, 0); // Define para o início do dia
    } catch (e) {
        console.error(`Data de abertura inválida para conta ${account.name} (${accountId}): ${openingDateStr}`);
        return startingBalance; // Retorna saldo inicial se a data for inválida
    }
  
  
    // 4. Filtra as transações relevantes
    const relevantTransactions = allTransactions.filter(t => {
        // Verifica se pertence à conta
        if (t.accountId !== accountId) return false;
  
        // Verifica se a transação ocorreu NA data de abertura OU DEPOIS
        let transactionDate;
        const transactionDateStr = t.data; // Usa a 'data' do lançamento (quando foi pago/efetivado)
        if (!transactionDateStr) return false; // Ignora transações sem data
  
        try {
            const parts = transactionDateStr.split('-');
            transactionDate = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
             if (isNaN(transactionDate.getTime())) throw new Error("Data inválida");
             transactionDate.setHours(0, 0, 0, 0); // Define para o início do dia
        } catch (e) {
            console.error(`Data inválida para transação ${t.id}: ${transactionDateStr}`);
            return false; // Ignora transação com data inválida
        }
  
        // Compara as datas (considera transações do dia da abertura)
        return transactionDate >= openingDate;
    });
  
    // 5. Itera sobre as transações e ajusta o saldo
    relevantTransactions.forEach(t => {
        const value = typeof t.valor === 'number' ? t.valor : 0;
        switch (t.tipo) {
            case 'receita':
            case 'aporte':
                currentBalance += value;
                break;
            case 'despesa':
            case 'retirada':
                currentBalance -= value;
                break;
            default:
                // Tipo de transação desconhecido, não faz nada ou loga um aviso
                console.warn(`Tipo de transação desconhecido encontrado: ${t.tipo} na transação ${t.id}`);
                break;
        }
    });
  
    // 6. Retorna o saldo final calculado
    return currentBalance;
  };